import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import queryString from 'query-string';
import { DebugInfo, Loading, SystemText } from '@salesperformanceint/e4shared';
import { setPrismMenu } from 'actions/ui';
import PageHeader from 'components/general/pageHeader';
import Toggle from 'components/prism/toggle';
import { apiGet } from 'services';
import Overview from './components/overview';
import Reviews from './components/reviews';
import Library from './components/library';
import Objectives from './components/objectives';
import Pathways from './components/pathways';
import PathwayDetail from './components/pathwayDetail';

export const TeamMember = ({ match, location, history }) => {
    const dispatch = useDispatch();
    const [profile, setProfile] = React.useState(null);
    const [loading, setLoading] = React.useState(true);
    const initialQuery = React.useMemo(() => queryString.parse(location.search));
    const [pathwayId, setPathwayId] = React.useState(() => initialQuery?.pathwayId ?? null);
    const [reviewId, setReviewId] = React.useState(() => initialQuery?.reviewId ?? null);
    const [objectiveId, setObjectiveId] = React.useState(() => initialQuery?.objectiveId ?? null);
    const [loadingStats, setLoadingStats] = React.useState(true);

    const [tab, setTab] = React.useState(() => {
        if (initialQuery?.pathwayId) return 'pathways';
        if (initialQuery?.tab) return initialQuery.tab;
        return 'overview';
    });

    const [launchedReviewsCount, setLaunchedReviewsCount] = React.useState(0);

    const { user } = useSelector(state => state.user);
    const profileId = match.params.id;

    const api = {
        loadAppraisals: profileId => {
            apiGet(`appraisal-count/${profileId}`, user.guid, response => {
                setLaunchedReviewsCount(response.scheduled);
            });
        },
    };

    React.useEffect(() => {
        if (initialQuery?.pathwayId || initialQuery?.reviewId || initialQuery?.objectiveId || initialQuery?.tab) {
            history.replace(location.pathname);
        }
    }, []);

    React.useEffect(() => {
        dispatch(setPrismMenu('Team'));
    }, []);

    React.useEffect(() => {
        DebugInfo('component', 'TeamMember');
    }, []);

    React.useEffect(() => {
        setLoading(true);
        if (profileId) {
            apiGet(`team-member/${profileId}`, user.guid, response => {
                setProfile(response);
                setLoading(false);
            });
        }
    }, [profileId]);

    // Load appraisals and calculate count of reviews with launch dates that have passed
    React.useEffect(() => {
        if (profileId) {
            api.loadAppraisals(profileId);
        }
    }, [profileId]);

    const reloadStats = () => {
        setLoadingStats(true);
        apiGet(`team-member/${profileId}`, user.guid, response => {
            setProfile(response);
            setLoadingStats(false);
        });
        // Also reload the launched reviews count
        if (profileId) {
            api.loadAppraisals(profileId);
        }
    };

    const mainContent = () => {
        switch (tab) {
            case 'reviews':
                return <Reviews profile={profile} reloadStats={reloadStats} reviewId={reviewId} />;
            case 'pathways':
                if (pathwayId) {
                    return <PathwayDetail pathwayId={pathwayId} profile={profile} />;
                }

                return <Pathways profile={profile} setPathwayId={setPathwayId} />;
            case 'library':
                return <Library profile={profile} />;
            case 'objectives':
                return <Objectives profile={profile} objectiveId={objectiveId} />;
            default:
                return <Overview profile={profile} />;
        }
    };

    return (
        <div>
            <PageHeader prismProfile={profile} team />
            <section className="content mtm10" data-testid="team-member-content">
                <Toggle
                    value={tab}
                    onChange={x => {
                        setTab(x);
                        setPathwayId(null);
                    }}
                >
                    <button value="overview">{SystemText('TeamMember/Overview')}</button>
                    <button value="reviews">
                        <div className="flex flex-center">
                            <span>{SystemText('TeamMember/Reviews')}</span>
                            {launchedReviewsCount > 0 && (
                                <span className="label label-danger ml10 btn-cornered">{launchedReviewsCount}</span>
                            )}
                        </div>
                    </button>
                    <button value="pathways">
                        <div className="flex flex-center">
                            <span>{SystemText('TeamMember/Pathways')}</span>
                        </div>
                    </button>
                    <button value="library">{SystemText('TeamMember/Library')}</button>
                    <button value="objectives">
                        <div className="flex flex-center">
                            <span>{SystemText('TeamMember/Objectives')}</span>
                            {profile?.awaiting > 0 && (
                                <span className="label label-danger ml10 btn-cornered">{profile.awaiting}</span>
                            )}
                        </div>
                    </button>
                </Toggle>
                {loading ? <Loading /> : mainContent()}
            </section>
        </div>
    );
};

export default TeamMember;
